import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTaskStore } from "@/lib/stores/task-store";

interface TaskAssignmentDialogProps {
  helperName: string;
  open: boolean;
  onClose: () => void;
  onAssign: (data: TaskAssignmentData) => void;
}

export interface TaskAssignmentData {
  title: string;
  description: string;
  priority: string;
  category: string;
  scheduledDate: Date;
  property: string;
}

export function TaskAssignmentDialog({ 
  helperName, 
  open, 
  onClose, 
  onAssign 
}: TaskAssignmentDialogProps) {
  const [date, setDate] = useState<Date>();
  const [selectedTaskId, setSelectedTaskId] = useState<string>("new");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "",
    category: "",
    property: "",
  });

  // Get unassigned tasks from the store
  const unassignedTasks = useTaskStore(
    (state) => state.tasks.filter((task) => !task.assignedTo)
  );

  const handleTaskSelect = (taskId: string) => {
    setSelectedTaskId(taskId);
    if (taskId === "new") {
      setFormData({
        title: "",
        description: "",
        priority: "",
        category: "",
        property: "",
      });
    } else {
      const selectedTask = unassignedTasks.find(task => task.id === taskId);
      if (selectedTask) {
        setFormData({
          title: selectedTask.title,
          description: selectedTask.description,
          priority: selectedTask.priority,
          category: selectedTask.category,
          property: selectedTask.propertyId,
        });
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) return;

    onAssign({
      ...formData,
      scheduledDate: date,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Assign Task to {helperName}</DialogTitle>
          <DialogDescription>
            Select an existing task or create a new one.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Select Task</Label>
            <Select
              value={selectedTaskId}
              onValueChange={handleTaskSelect}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a task" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Create New Task
                  </div>
                </SelectItem>
                {unassignedTasks.map((task) => (
                  <SelectItem key={task.id} value={task.id}>
                    <div className="flex flex-col">
                      <span>{task.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {task.description.slice(0, 50)}...
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rest of the form remains the same */}
          <div className="space-y-2">
            <Label htmlFor="title">Task Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter task title"
              required
              disabled={selectedTaskId !== "new"}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the task"
              required
              disabled={selectedTaskId !== "new"}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
                required
                disabled={selectedTaskId !== "new"}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
                required
                disabled={selectedTaskId !== "new"}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="installation">Installation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Schedule Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Property</Label>
            <Select
              value={formData.property}
              onValueChange={(value) => setFormData({ ...formData, property: value })}
              required
              disabled={selectedTaskId !== "new"}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="property1">Sunset Apartments</SelectItem>
                <SelectItem value="property2">Oak Residences</SelectItem>
                <SelectItem value="property3">Pine View Complex</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1">
              Assign Task
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}